import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  ChevronLeft, Plus, Trash2, Save, ArrowUp, ArrowDown, Copy, X,
  Link as LinkIcon, ExternalLink, ChevronRight, ChevronDown, LayoutTemplate,
  UserRound, StickyNote, Pencil,
} from "lucide-react";
import { toast } from "sonner";

// Compact task-row inside the template editor.
// Default state = one-liner (Action + inline chips). Click chevron to expand
// for Notes / Owner / Links editing.
const emptyTask = () => ({ action: "", notes: "", owner_id: "", links: [] });
const emptySection = () => ({ title: "New section", tasks: [] });

export default function OnboardingTemplateEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id || id === "new";

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [users, setUsers] = useState([]);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [sections, setSections] = useState([]);
  const [activeIdx, setActiveIdx] = useState(0);

  useEffect(() => {
    (async () => {
      const u = await api.get("/users").catch(() => ({ data: [] }));
      setUsers(u.data || []);
      if (!isNew) {
        try {
          const r = await api.get("/onboarding/templates");
          const tpl = r.data.find((t) => t.id === id);
          if (!tpl) { toast.error("Template not found"); navigate("/app/onboarding"); return; }
          setName(tpl.name || "");
          setDescription(tpl.description || "");
          setSections((tpl.sections || []).map((s) => ({
            id: s.id,
            title: s.title || "",
            collapsed: false,
            tasks: (s.tasks || []).map((t) => ({
              id: t.id,
              action: t.action || "",
              notes: t.notes || "",
              owner_id: t.owner_id || "",
              links: [...(t.links || [])],
              expanded: false,
            })),
          })));
        } catch (_) { toast.error("Failed to load"); }
        setLoading(false);
      }
    })();
  }, [id, isNew, navigate]);

  const totalTasks = useMemo(() => sections.reduce((n, s) => n + s.tasks.length, 0), [sections]);

  // ----- Section-level ops -----
  const addSection = () => {
    setSections((s) => [...s, emptySection()]);
    setActiveIdx(sections.length); // jump to new
  };
  const updateSection = (idx, patch) =>
    setSections((s) => s.map((sec, i) => (i === idx ? { ...sec, ...patch } : sec)));
  const removeSection = (idx) => {
    if (!window.confirm(`Delete section "${sections[idx].title}"?`)) return;
    setSections((s) => s.filter((_, i) => i !== idx));
    setActiveIdx((cur) => Math.max(0, Math.min(cur - (cur >= idx ? 1 : 0), sections.length - 2)));
  };
  const moveSection = (idx, dir) => {
    const to = idx + dir;
    if (to < 0 || to >= sections.length) return;
    setSections((s) => {
      const next = [...s]; const [row] = next.splice(idx, 1); next.splice(to, 0, row); return next;
    });
    setActiveIdx(to);
  };

  // ----- Task-level ops -----
  const updateTask = (si, ti, patch) =>
    setSections((s) => s.map((sec, i) => i === si
      ? { ...sec, tasks: sec.tasks.map((t, j) => (j === ti ? { ...t, ...patch } : t)) }
      : sec));
  const addTask = (si) =>
    setSections((s) => s.map((sec, i) => i === si
      ? { ...sec, tasks: [...sec.tasks, { ...emptyTask(), expanded: true }] } : sec));
  const removeTask = (si, ti) =>
    setSections((s) => s.map((sec, i) => i === si
      ? { ...sec, tasks: sec.tasks.filter((_, j) => j !== ti) } : sec));
  const duplicateTask = (si, ti) =>
    setSections((s) => s.map((sec, i) => i === si
      ? { ...sec, tasks: [...sec.tasks.slice(0, ti + 1), { ...sec.tasks[ti], id: undefined }, ...sec.tasks.slice(ti + 1)] }
      : sec));
  const moveTask = (si, ti, dir) =>
    setSections((s) => s.map((sec, i) => {
      if (i !== si) return sec;
      const to = ti + dir;
      if (to < 0 || to >= sec.tasks.length) return sec;
      const arr = [...sec.tasks]; const [row] = arr.splice(ti, 1); arr.splice(to, 0, row);
      return { ...sec, tasks: arr };
    }));

  const save = async () => {
    if (!name.trim()) return toast.error("Template name is required");
    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        description: description.trim(),
        sections: sections.map((s) => ({
          title: s.title.trim() || "Section",
          tasks: s.tasks.filter((t) => t.action.trim()).map((t) => ({
            action: t.action.trim(),
            notes: (t.notes || "").trim(),
            owner_id: t.owner_id || null,
            links: (t.links || []).filter((l) => (l.url || "").trim()),
          })),
        })),
      };
      if (isNew) await api.post("/onboarding/templates", payload);
      else await api.patch(`/onboarding/templates/${id}`, payload);
      toast.success(isNew ? "Template created" : "Template saved");
      navigate("/app/onboarding");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-10 text-center text-sm text-slate-500">Loading…</div>;

  const active = sections[activeIdx];

  return (
    <div className="space-y-4 pb-24" data-testid="onboarding-template-editor">
      {/* Sticky header */}
      <div className="sticky top-0 z-10 -mx-4 sm:mx-0 bg-white/95 backdrop-blur border-b border-slate-200 px-4 sm:px-6 py-3 flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate("/app/onboarding")}>
          <ChevronLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] font-mono uppercase tracking-wider text-blue-600 flex items-center gap-1">
            <LayoutTemplate className="h-3 w-3" /> On-boarding template
          </div>
          <div className="font-heading font-bold text-lg text-slate-900 truncate">
            {isNew ? "New template" : name || "Template"}
          </div>
        </div>
        <div className="hidden sm:flex items-center gap-3 text-xs text-slate-500">
          <span><b>{sections.length}</b> sections</span>
          <span><b>{totalTasks}</b> tasks</span>
        </div>
        <Button onClick={save} disabled={saving} className="bg-blue-600 hover:bg-blue-700" data-testid="save-template">
          <Save className="h-4 w-4 mr-1" /> {saving ? "Saving…" : "Save template"}
        </Button>
      </div>

      {/* Meta card */}
      <Card className="border-slate-200">
        <CardContent className="p-5 grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="md:col-span-1">
            <label className="text-[10px] font-mono uppercase text-slate-500">Template name *</label>
            <Input data-testid="template-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Xentra Sol — Onboarding Action List" />
          </div>
          <div className="md:col-span-2">
            <label className="text-[10px] font-mono uppercase text-slate-500">Description (optional)</label>
            <Input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Short summary of what this template covers" />
          </div>
        </CardContent>
      </Card>

      {/* Two-pane editor */}
      <div className="grid grid-cols-1 lg:grid-cols-[280px,1fr] gap-4">
        {/* SECTIONS PANE */}
        <Card className="border-slate-200 h-fit lg:sticky lg:top-24" data-testid="sections-pane">
          <CardHeader className="border-b border-slate-100 py-2.5 flex flex-row items-center justify-between">
            <CardTitle className="text-xs font-mono uppercase tracking-wider text-slate-500">Sections</CardTitle>
            <Button size="sm" variant="ghost" onClick={addSection} className="h-6 px-2" data-testid="add-section">
              <Plus className="h-3 w-3 mr-1" />New
            </Button>
          </CardHeader>
          <CardContent className="p-2">
            {sections.length === 0 ? (
              <div className="text-center text-xs text-slate-400 py-6">
                No sections yet.<br />Click <b>+ New</b> to add one.
              </div>
            ) : (
              <ul className="space-y-1">
                {sections.map((sec, i) => (
                  <li key={i}>
                    <button
                      onClick={() => setActiveIdx(i)}
                      data-testid={`section-tab-${i}`}
                      className={`w-full flex items-center gap-2 px-2.5 py-2 rounded-md text-left text-sm transition ${activeIdx === i ? "bg-blue-50 border border-blue-200 text-blue-900" : "hover:bg-slate-50 border border-transparent text-slate-700"}`}
                    >
                      <span className="text-[10px] font-mono text-slate-400 w-4 text-right">{i + 1}</span>
                      <span className="flex-1 truncate">{sec.title || "Untitled"}</span>
                      <Badge variant="outline" className="text-[10px] flex-shrink-0">{sec.tasks.length}</Badge>
                    </button>
                    {activeIdx === i && (
                      <div className="flex items-center justify-end gap-0.5 px-1 py-1">
                        <button onClick={() => moveSection(i, -1)} disabled={i === 0} className="disabled:opacity-30 text-slate-400 hover:text-blue-600 p-0.5" title="Move up"><ArrowUp className="h-3 w-3" /></button>
                        <button onClick={() => moveSection(i, 1)} disabled={i === sections.length - 1} className="disabled:opacity-30 text-slate-400 hover:text-blue-600 p-0.5" title="Move down"><ArrowDown className="h-3 w-3" /></button>
                        <button onClick={() => removeSection(i)} className="text-slate-400 hover:text-rose-600 p-0.5 ml-auto" title="Delete section"><Trash2 className="h-3 w-3" /></button>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        {/* ACTIVE SECTION DETAIL */}
        {active ? (
          <div className="space-y-3">
            <Card className="border-slate-200">
              <CardContent className="p-4">
                <label className="text-[10px] font-mono uppercase text-slate-500">Section title</label>
                <Input
                  data-testid="active-section-title"
                  value={active.title}
                  onChange={(e) => updateSection(activeIdx, { title: e.target.value })}
                  className="font-heading text-lg"
                  placeholder="e.g. Pre-boarding · IT & Equipment"
                />
              </CardContent>
            </Card>

            <div className="flex items-center justify-between">
              <div className="text-xs text-slate-500">{active.tasks.length} task{active.tasks.length === 1 ? "" : "s"} in this section</div>
              <Button size="sm" onClick={() => addTask(activeIdx)} className="bg-blue-600 hover:bg-blue-700" data-testid="add-task">
                <Plus className="h-4 w-4 mr-1" /> Add task
              </Button>
            </div>

            <div className="space-y-2">
              {active.tasks.length === 0 ? (
                <Card className="border-dashed">
                  <CardContent className="p-8 text-center text-sm text-slate-400">
                    No tasks yet. Click <b>Add task</b> to build the checklist.
                  </CardContent>
                </Card>
              ) : (
                active.tasks.map((task, ti) => (
                  <TaskCard
                    key={ti}
                    task={task}
                    users={users}
                    testid={`task-card-${ti}`}
                    onChange={(patch) => updateTask(activeIdx, ti, patch)}
                    onDelete={() => removeTask(activeIdx, ti)}
                    onDuplicate={() => duplicateTask(activeIdx, ti)}
                    onMoveUp={ti > 0 ? () => moveTask(activeIdx, ti, -1) : null}
                    onMoveDown={ti < active.tasks.length - 1 ? () => moveTask(activeIdx, ti, 1) : null}
                    indexLabel={ti + 1}
                  />
                ))
              )}
            </div>
          </div>
        ) : (
          <Card className="border-dashed"><CardContent className="p-10 text-center text-sm text-slate-500">
            Add a section on the left to start building the template.
          </CardContent></Card>
        )}
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// TaskCard — compact by default (single line), expandable for full edit
// -----------------------------------------------------------------------------
function TaskCard({ task, users, onChange, onDelete, onDuplicate, onMoveUp, onMoveDown, indexLabel, testid }) {
  const ownerName = users.find((u) => u.user_id === task.owner_id);
  const hasDetails = (task.notes || "").trim() || (task.links || []).length > 0 || task.owner_id;
  const isNew = !task.action;
  const [expanded, setExpanded] = useState(task.expanded || isNew);

  return (
    <div
      className={`rounded-lg border transition ${expanded ? "border-blue-300 bg-blue-50/30 shadow-sm" : "border-slate-200 bg-white hover:border-slate-300"}`}
      data-testid={testid}
    >
      {/* Compact row */}
      <div className="flex items-start gap-2 p-3">
        <span className="text-[10px] font-mono text-slate-400 mt-2 w-4 text-right flex-shrink-0">{indexLabel}</span>
        <div className="flex-1 min-w-0">
          <Input
            value={task.action}
            onChange={(e) => onChange({ action: e.target.value })}
            placeholder="Task action (e.g. Provision M365 account)"
            className="border-0 shadow-none focus-visible:ring-1 bg-transparent text-sm font-medium px-2"
          />
          {!expanded && hasDetails && (
            <div className="flex flex-wrap gap-1.5 mt-1 px-2">
              {task.owner_id && ownerName && (
                <Badge variant="outline" className="text-[10px]">
                  <UserRound className="h-2.5 w-2.5 mr-0.5" />{ownerName.name || ownerName.email}
                </Badge>
              )}
              {(task.notes || "").trim() && (
                <Badge variant="outline" className="text-[10px]">
                  <StickyNote className="h-2.5 w-2.5 mr-0.5" />{task.notes.length > 40 ? task.notes.slice(0, 40) + "…" : task.notes}
                </Badge>
              )}
              {(task.links || []).length > 0 && (
                <Badge variant="outline" className="text-[10px]">
                  <LinkIcon className="h-2.5 w-2.5 mr-0.5" />{task.links.length} link{task.links.length === 1 ? "" : "s"}
                </Badge>
              )}
            </div>
          )}
        </div>
        <div className="flex items-center gap-0.5 flex-shrink-0">
          <button
            onClick={() => setExpanded((x) => !x)}
            className={`p-1 rounded ${expanded ? "text-blue-600 bg-blue-100" : "text-slate-400 hover:text-blue-600 hover:bg-slate-100"}`}
            title={expanded ? "Collapse" : "Expand"}
            data-testid={`task-toggle-${indexLabel}`}
          >
            {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          </button>
          {onMoveUp && <button onClick={onMoveUp} title="Move up" className="p-1 text-slate-400 hover:text-blue-600"><ArrowUp className="h-3.5 w-3.5" /></button>}
          {onMoveDown && <button onClick={onMoveDown} title="Move down" className="p-1 text-slate-400 hover:text-blue-600"><ArrowDown className="h-3.5 w-3.5" /></button>}
          <button onClick={onDuplicate} title="Duplicate" className="p-1 text-slate-400 hover:text-blue-600"><Copy className="h-3.5 w-3.5" /></button>
          <button onClick={onDelete} title="Delete" className="p-1 text-slate-400 hover:text-rose-600"><Trash2 className="h-3.5 w-3.5" /></button>
        </div>
      </div>

      {/* Expanded editor */}
      {expanded && (
        <div className="border-t border-blue-200 px-3 py-3 space-y-3 bg-white/60">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] font-mono uppercase text-slate-500 flex items-center gap-1">
                <UserRound className="h-3 w-3" /> Default owner
              </label>
              <Select value={task.owner_id || "unassigned"} onValueChange={(v) => onChange({ owner_id: v === "unassigned" ? "" : v })}>
                <SelectTrigger><SelectValue placeholder="Unassigned" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="unassigned">Unassigned (line manager to assign)</SelectItem>
                  {users.map((u) => <SelectItem key={u.user_id} value={u.user_id}>{u.name || u.email}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <label className="text-[10px] font-mono uppercase text-slate-500 flex items-center gap-1">
              <StickyNote className="h-3 w-3" /> Notes
            </label>
            <Textarea rows={3} value={task.notes} onChange={(e) => onChange({ notes: e.target.value })} placeholder="Anything the owner needs to know. Line breaks are preserved." />
          </div>
          <LinksField links={task.links || []} onChange={(links) => onChange({ links })} />
        </div>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// LinksField — one row per link (Label + URL)
// -----------------------------------------------------------------------------
function LinksField({ links, onChange }) {
  const add = () => onChange([...(links || []), { label: "", url: "" }]);
  const update = (i, patch) => onChange(links.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  const remove = (i) => onChange(links.filter((_, idx) => idx !== i));

  return (
    <div>
      <label className="text-[10px] font-mono uppercase text-slate-500 flex items-center gap-1 mb-1">
        <LinkIcon className="h-3 w-3" /> Links
      </label>
      <div className="space-y-1.5">
        {(links || []).length === 0 && (
          <div className="text-[11px] text-slate-400">No links yet.</div>
        )}
        {(links || []).map((l, i) => (
          <div key={i} className="flex gap-1.5 items-center">
            <Input className="w-32 text-xs" placeholder="Label" value={l.label || ""} onChange={(e) => update(i, { label: e.target.value })} />
            <Input className="flex-1 text-xs" placeholder="https://…" value={l.url || ""} onChange={(e) => update(i, { url: e.target.value })} />
            {(l.url || "").trim() && (
              <a href={l.url} target="_blank" rel="noreferrer" className="text-slate-400 hover:text-blue-600 p-1" title="Open"><ExternalLink className="h-3 w-3" /></a>
            )}
            <button onClick={() => remove(i)} className="text-slate-400 hover:text-rose-600 p-1" title="Remove"><X className="h-3 w-3" /></button>
          </div>
        ))}
        <Button size="sm" variant="ghost" onClick={add} className="h-7 text-xs"><Plus className="h-3 w-3 mr-1" />Add link</Button>
      </div>
    </div>
  );
}
